import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Receipt, Plus, Check, X as XIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";

const CATS = ["travel", "food", "accommodation", "internet", "other"];
const fmt = (n) => `₹${(n||0).toLocaleString("en-IN")}`;

export default function Claims() {
  const { user } = useAuth();
  const isApprover = ["super_admin", "hr_admin", "finance", "manager", "department_head", "team_lead"].includes(user?.role);
  const [claims, setClaims] = useState([]);
  const [tab, setTab] = useState("mine");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "travel", amount: "", expense_date: "", description: "", receipt_file_id: "" });
  const [detail, setDetail] = useState(null);
  const [cmt, setCmt] = useState("");

  const load = async () => {
    const r = await api.get("/claims", { params: { scope: tab } });
    setClaims(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const uploadReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) { toast.error("Max 3MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const up = await api.post("/files", { name: f.name, mime_type: f.type, data_url: reader.result, category: "claim" });
        setForm(prev => ({ ...prev, receipt_file_id: up.data.id }));
        toast.success("Receipt attached");
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(f);
  };

  const create = async () => {
    try {
      await api.post("/claims", { ...form, amount: Number(form.amount) });
      toast.success("Claim submitted"); setOpen(false);
      setForm({ category: "travel", amount: "", expense_date: "", description: "", receipt_file_id: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const act = async (id, action) => {
    try { await api.post(`/claims/${id}/action`, { action, comments: cmt }); toast.success(action === "approve" ? "Approved" : "Rejected"); setDetail(null); setCmt(""); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="claims-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Services</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Claims & Reimbursements</h1>
          <p className="text-sm text-slate-600 mt-1">Submit expenses and track approvals</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-claim-btn"><Plus className="w-4 h-4 mr-1"/>New claim</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit expense claim</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATS.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="mt-1" data-testid="claim-amount"/></div>
              </div>
              <div><Label>Expense date</Label><Input type="date" value={form.expense_date} onChange={(e) => setForm({...form, expense_date: e.target.value})} className="mt-1"/></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="mt-1"/></div>
              <div><Label>Receipt (image/PDF)</Label><Input type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="mt-1"/>{form.receipt_file_id && <div className="text-[11px] text-emerald-700 mt-1">✓ Receipt attached</div>}</div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.amount || !form.expense_date} className="bg-slate-900" data-testid="claim-submit">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mine">My claims</TabsTrigger>
          {isApprover && <TabsTrigger value="pending-me">Pending my approval</TabsTrigger>}
          {isApprover && <TabsTrigger value="all">All claims</TabsTrigger>}
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                  <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {claims.map(c => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`claim-row-${c.id}`}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-7 h-7"><AvatarImage src={c.employee?.avatar_url}/><AvatarFallback className="bg-slate-900 text-white text-[10px]">{(c.employee?.full_name||"--").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                        <div className="text-slate-900">{c.employee?.full_name || "—"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize">{c.category}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{fmt(c.amount)}</td>
                    <td className="px-4 py-3 text-slate-600">{c.expense_date}</td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 capitalize ${c.status === "approved" ? "bg-emerald-100 text-emerald-800" : c.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{c.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(c)}>View</Button>
                    </td>
                  </tr>
                ))}
                {claims.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-400"><Receipt className="w-8 h-8 mx-auto mb-2 text-slate-300"/>No claims in this view.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Claim details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-slate-500">Category</div><div className="capitalize font-medium">{detail.category}</div></div>
                <div><div className="text-xs text-slate-500">Amount</div><div className="font-[Outfit] font-semibold text-xl">{fmt(detail.amount)}</div></div>
                <div><div className="text-xs text-slate-500">Expense date</div><div>{detail.expense_date}</div></div>
                <div><div className="text-xs text-slate-500">Status</div><Badge className={`capitalize ${detail.status === "approved" ? "bg-emerald-100 text-emerald-800" : detail.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{detail.status}</Badge></div>
              </div>
              {detail.description && <div><div className="text-xs text-slate-500">Description</div><div className="p-2 bg-slate-50 rounded-md">{detail.description}</div></div>}
              {detail.receipt_file_id && <div><a href="#" onClick={async (e) => { e.preventDefault(); const r = await api.get(`/files/${detail.receipt_file_id}`); window.open(r.data.data_url); }} className="text-cyan-700 underline">View attached receipt</a></div>}
              {detail.approver_comments && <div><div className="text-xs text-slate-500">Approver comments</div><div>{detail.approver_comments}</div></div>}

              {isApprover && detail.status === "pending" && (
                <div className="pt-2 border-t space-y-2">
                  <Input placeholder="Optional comment" value={cmt} onChange={(e) => setCmt(e.target.value)}/>
                  <div className="flex gap-2">
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(detail.id, "approve")}><Check className="w-3 h-3 mr-1"/>Approve</Button>
                    <Button variant="outline" className="text-red-600 border-red-300" onClick={() => act(detail.id, "reject")}><XIcon className="w-3 h-3 mr-1"/>Reject</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
